import { CheckCircle2, ExternalLink, Loader2, ShieldCheck, Wallet, X, XCircle } from 'lucide-react';
import { formatINR } from '../lib/money';

/**
 * The payment gateway, hosted by us.
 *
 * Full-bleed on purpose. As a floating card this read as "a website inside a
 * box": our dark header, then the provider's own header, then its centred card
 * with our margins around it, then our grey footer bar. Three frames for one
 * form. It is still a modal — the customer never leaves the page and the result
 * lands back here — but it should not announce that it is one.
 *
 * So: the provider's page owns the whole viewport below one slim bar, and our
 * only other chrome is a pill floating over it. The bar is light rather than the
 * old navy, because it sits directly above a light page and a dark slab there
 * drew a hard line exactly where we did not want one.
 *
 * The width matters and is not cosmetic: Easebuzz is responsive to the frame's
 * width, not the window's, and below roughly 1024px it collapses to a phone
 * layout with the QR hidden behind two more taps. Full-bleed clears that on any
 * desktop.
 *
 * One copy, two callers — the product page and the cart both settle payments
 * and neither should own a private version of this.
 */
export function GiftCardGatewayModal({
  open,
  url,
  orderId,
  amount,
  status = 'pending',
  checking = false,
  /** e.g. "card 2 of 6" while a basket is walked one payment at a time */
  progressLabel,
  /** {refunded, amount} — set when a failed order was paid for and repaid. */
  refund,
  onCheckStatus,
  onClose,
  onRetry,
}) {
  if (!open) return null;

  const closable = status === 'pending';

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col bg-white" data-testid="gateway-modal">
      {/* Slim bar — the only chrome above the provider's page. */}
      <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-2.5 sm:px-6">
        <div className="flex min-w-0 items-center gap-3">
          {closable && (
            <button
              onClick={onClose}
              aria-label="Cancel payment"
              className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-surface hover:text-secondary"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 font-body text-[13px] font-semibold leading-tight text-secondary">
              <ShieldCheck className="h-3.5 w-3.5 flex-shrink-0 text-emerald-500" />
              Secure checkout
              {progressLabel && (
                <span className="font-normal text-muted-foreground">· {progressLabel}</span>
              )}
            </p>
            <p className="truncate font-body text-[11px] text-muted-foreground">
              Order #{orderId}
            </p>
          </div>
        </div>

        {amount > 0 && (
          <div className="flex-shrink-0 text-right">
            <p className="font-body text-[10px] uppercase tracking-wider text-muted-foreground">
              Paying
            </p>
            <p className="font-body text-[15px] font-bold leading-tight text-secondary">
              {formatINR(amount)}
            </p>
          </div>
        )}
      </div>

      {status === 'pending' && (
        <div className="relative min-h-0 flex-1">
          <iframe
            src={url}
            title="Payment Gateway"
            className="h-full w-full border-0"
            allow="payment"
          />

          {/*
            Floating rather than a footer bar. A solid strip across the bottom
            re-drew the frame we just removed, and the provider's own "Pay"
            button lives down there — a full-width button of ours beside it gave
            the screen two competing primary actions, and customers pressed ours
            instead of paying.
          */}
          <div className="pointer-events-none absolute inset-x-0 bottom-4 flex justify-center px-4">
            <div className="pointer-events-auto flex max-w-full flex-wrap items-center justify-center gap-x-3 gap-y-1.5 rounded-full border border-slate-200 bg-white/95 px-4 py-2 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.35)] backdrop-blur">
              <span className="inline-flex items-center gap-2 font-body text-[12px] text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                Waiting for payment — confirms automatically
              </span>

              <span className="hidden h-4 w-px bg-slate-200 sm:block" />

              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-body text-[12px] font-semibold text-muted-foreground transition-colors hover:text-secondary"
              >
                <ExternalLink className="h-3.5 w-3.5" /> New tab
              </a>

              <button
                onClick={onCheckStatus}
                disabled={checking}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1 font-body text-[12px] font-semibold text-secondary transition-colors hover:border-primary/40 hover:text-primary disabled:opacity-60"
              >
                {checking
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking…</>
                  : <><CheckCircle2 className="h-3.5 w-3.5" /> Check status</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {status === 'success' && (
        <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
          <div className="mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-emerald-50 ring-[10px] ring-emerald-100">
            <CheckCircle2 className="h-12 w-12 text-emerald-500" />
          </div>
          <h2 className="font-heading text-2xl text-secondary">Payment successful</h2>
          <p className="mt-2 font-body text-sm text-muted-foreground">Issuing your voucher…</p>
        </div>
      )}

      {status === 'failed' && (
        <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
          {/*
            Two different events share the name "failed" and the difference is
            the customer's money. The backend auto-refunds to the wallet when the
            provider refuses after payment has cleared, so "nothing was charged"
            is only true when the gateway leg never completed — saying it in the
            other case sends the customer hunting a refund that already arrived.
          */}
          <div className={`mb-6 flex h-24 w-24 items-center justify-center rounded-full ${refund?.refunded
            ? 'bg-amber-50 ring-[10px] ring-amber-100'
            : 'bg-red-50 ring-[10px] ring-red-100'}`}>
            {refund?.refunded
              ? <Wallet className="h-12 w-12 text-amber-500" />
              : <XCircle className="h-12 w-12 text-red-500" />}
          </div>

          <h2 className="font-heading text-2xl text-secondary">
            {refund?.refunded ? 'Refunded to your wallet' : 'Payment failed'}
          </h2>

          <p className="mt-2 max-w-sm font-body text-sm leading-relaxed text-muted-foreground">
            {refund?.refunded
              ? <>The card could not be issued, so {refund.amount ? <strong className="text-secondary">{formatINR(refund.amount)}</strong> : 'your payment'} has been credited to your NSO wallet. It is there now — you can spend it or try again.</>
              : 'Nothing was charged. You can try again with the same or a different method.'}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {onRetry && !refund?.refunded && (
              <button
                onClick={onRetry}
                className="rounded-full bg-primary px-8 py-3 font-body text-sm font-semibold text-white transition-all hover:bg-primary/90 active:scale-95"
              >
                Retry payment
              </button>
            )}
            <button
              onClick={onClose}
              className="rounded-full border-2 border-gray-200 px-8 py-3 font-body text-sm font-semibold text-gray-500 transition-all hover:bg-gray-50 active:scale-95"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default GiftCardGatewayModal;
